import { useTheme } from '@material-ui/core/styles';
import { SortingMode } from '@src/model/frontendmodel';
import { activeChatSideSelector } from '@src/selectors/chats';
import { preferencesSelect } from '@src/selectors/preferences';
import { ChatSideType, ChatTray } from '@src/types/chats';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  differenceInCalendarDays,
  format,
  isToday,
  isYesterday,
} from 'date-fns';
import { es } from 'date-fns/locale';
import Typography from '../UI/Typography';

const ChatSmartFormatDate: React.FC<{ chat: ChatTray }> = ({ chat }) => {
  const theme = useTheme();
  const activeChatSide = useSelector(activeChatSideSelector);
  const preferences = useSelector(preferencesSelect);

  const isChatRequest = activeChatSide === ChatSideType.REQUEST;

  const chatTimeLabel = useMemo(() => {
    const sortingMerchantModeName = (
      isChatRequest
        ? preferences.sortModeIncomingChats
        : preferences.sortModeOngoingChats
    ) as keyof typeof SortingMode;

    const sortingMerchantMode =
      SortingMode[sortingMerchantModeName] ??
      SortingMode.LAST_RECEIVED_MESSAGE_FIRST;

    if (
      [
        SortingMode.LAST_RECEIVED_MESSAGE_FIRST,
        SortingMode.CHAT_SEGMENT_PRIORITY_LIFO,
        SortingMode.CHAT_SEGMENT_PRIORITY_FIFO,
        SortingMode.OLDEST_CREATED_FIRST,
      ].includes(sortingMerchantMode)
    ) {
      const lastReceivedTime = chat?.lastMsgReceivedAt || 0;
      const lastSentTime = chat?.lastMsgSentAt || 0;

      const lastMessageTime =
        lastReceivedTime >= lastSentTime ? lastReceivedTime : lastSentTime;

      if (lastMessageTime) return lastMessageTime;
    }

    return chat?.lastMessage?.sendingTime || chat?.lastMsgReceivedAt;
  }, [
    chat,
    isChatRequest,
    preferences.sortModeIncomingChats,
    preferences.sortModeOngoingChats,
  ]);

  return (
    <Typography
      style={{
        fontSize: '.7em',
        textTransform: 'capitalize',
        color: theme.palette.text.secondary,
      }}
    >
      {Boolean(chatTimeLabel) && smartFormatDate(chatTimeLabel)}
    </Typography>
  );
};

export function smartFormatDate(
  value: Date | string | number,
  now: Date = new Date()
): string {
  const isDate = !(typeof value === 'string' || typeof value === 'number');
  const date = isDate ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  if (isToday(date)) return format(date, 'hh:mm a');
  if (isYesterday(date)) return 'Ayer';

  const diff = differenceInCalendarDays(now, date);
  if (diff > 1 && diff < 7) return format(date, 'eeee', { locale: es });

  //diff >= 7
  return format(date, 'dd/MM/yy');
}

export default ChatSmartFormatDate;
